import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
} from '@/components/ui';
import { ACCOUNT_TONES, MEMBER_TONES, humanizeStatus, roleLabel } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * The people who do the hiring.
 *
 * `Companies` lists organisations and `Users` lists accounts; neither answers
 * "who is working inside them?". A recruiter is a *seat* -- a row in
 * company_members carrying the workspace role a company user's real ability
 * comes from -- so one person invited into two workspaces is two rows here,
 * which is correct: two jobs, two histories.
 *
 * The activity column is what they have done rather than what their role
 * permits, because a dormant seat and a busy one look identical otherwise.
 */
/** The seat filter, as tabs. Same values the dropdown sent. */
const SEAT_TABS = [
  { key: '', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'INVITED', label: 'Invited' },
  { key: 'REMOVED', label: 'Removed' },
];

const PAGE_SIZE = 20;

export function AdminRecruitersPage() {
  // The workspace filter lives in the URL so the link from a company record
  // survives a reload and can be handed to somebody else.
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get('company_id') ?? '';
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [seat, setSeat] = useState('');
  const [account, setAccount] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(role ? { roles: role } : {}),
    ...(seat ? { member_statuses: seat } : {}),
    ...(account ? { statuses: account } : {}),
    ...(companyId ? { company_id: companyId } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-recruiters', params],
    queryFn: () => adminApi.listRecruiters(params),
    // Without this every keystroke in the search box unmounts the page for the
    // loading state -- taking the box, and the focus in it, with it.
    placeholderData: keepPreviousData,
  });

  /** Every filter change is a new result set, so page 1 is the only sane page. */
  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  // The workspace in the URL counts as a filter too: an empty list scoped to
  // one company is "nothing matches", not "nobody on the platform".
  const activeCount = [
    search !== '',
    role !== '',
    seat !== '',
    account !== '',
    companyId !== '',
  ].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setRole('');
    setSeat('');
    setAccount('');
    if (companyId) setSearchParams({}, { replace: true });
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Recruiters"
        description="Everybody holding a seat in a company workspace, and what they have done with it."
        action={
          <LinkButton to={paths.admin.companies} variant="secondary" size="sm">
            Companies
          </LinkButton>
        }
      />

      {companyId && (
        <p className="text-ink-muted mb-3 text-sm">
          Showing one workspace{rows[0] ? `: ${rows[0].company_name}` : ''}.{' '}
          <button
            type="button"
            className="text-brand-700 hover:underline"
            onClick={() => setSearchParams({}, { replace: true })}
          >
            Show every company
          </button>
        </p>
      )}

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Seat"
            tabs={SEAT_TABS}
            value={seat}
            onChange={(key) => {
              setSeat(key);
              setPage(1);
            }}
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Workspace role"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={role}
              onChange={change(setRole)}
            >
              <option value="">Any role</option>
              <option value="COMPANY_ADMIN">Company admin</option>
              <option value="HR">HR</option>
              <option value="RECRUITER">Recruiter</option>
              <option value="HIRING_MANAGER">Hiring manager</option>
            </Select>
            <Select
              label="Account"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={account}
              onChange={change(setAccount)}
            >
              <option value="">Any account status</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING_VERIFICATION">Pending verification</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="BANNED">Banned</option>
            </Select>
            <SearchInput
              label="Search recruiters"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Name, email, company or job title"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading recruiters…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="No recruiters"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No recruiters yet"
                description="Seats appear here once companies invite their team."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              // A restricted account changes how the whole seat reads, so its
              // colour wins over the seat's own.
              accent={
                row.account_status !== 'ACTIVE'
                  ? (ACCOUNT_TONES[row.account_status] ?? 'neutral')
                  : (MEMBER_TONES[row.member_status] ?? 'neutral')
              }
              leading={<BoxAvatar name={row.full_name} round />}
              title={row.full_name}
              to={paths.admin.recruiterDetail(row.id)}
              badges={
                <>
                  <Badge dot tone={MEMBER_TONES[row.member_status] ?? 'neutral'}>
                    {row.member_status === 'INVITED'
                      ? 'Invited'
                      : humanizeStatus(row.member_status)}
                  </Badge>
                  {/* Only the exception is worth a badge: an account in any
                      other state than active changes how the row is read. */}
                  {row.account_status !== 'ACTIVE' && (
                    <Badge tone={ACCOUNT_TONES[row.account_status] ?? 'neutral'}>
                      {humanizeStatus(row.account_status)}
                    </Badge>
                  )}
                  {row.is_owner && <Badge tone="brand">Owner</Badge>}
                </>
              }
              subtitle={row.email}
              facts={[
                {
                  icon: 'building',
                  text: (
                    <>
                      <Link
                        to={paths.admin.companyDetail(row.company_id)}
                        className="text-ink hover:text-brand-700 hover:underline"
                      >
                        {row.company_name}
                      </Link>
                      <span>· {humanizeStatus(row.company_verification_status)}</span>
                    </>
                  ),
                },
                {
                  icon: 'idBadge',
                  text: [roleLabel(row.role), row.job_title].filter(Boolean).join(' · '),
                },
                {
                  icon: 'clipboard',
                  text: `${row.decisions_count} ${
                    row.decisions_count === 1 ? 'decision' : 'decisions'
                  }`,
                },
                row.interviews_count > 0 && {
                  icon: 'calendarClock',
                  text: `${row.interviews_count} ${row.interviews_count === 1 ? 'interview' : 'interviews'}`,
                },
                row.offers_count > 0 && {
                  icon: 'contract',
                  text: `${row.offers_count} ${row.offers_count === 1 ? 'offer' : 'offers'}`,
                },
                {
                  icon: 'clockOutline',
                  text: `Last seen ${row.last_login_at ? formatDate(row.last_login_at) : 'never'}`,
                },
              ]}
              actions={
                <ViewButton
                  to={paths.admin.recruiterDetail(row.id)}
                  title={`Open ${row.full_name}`}
                />
              }
            />
          ))}
        </BoxList>
      </ListCard>

      <p className="text-ink-muted mt-3 text-xs">
        A seat is not an account: the same person can hold one in two workspaces and appears once
        per seat. Activity is counted per account, so those two rows show the same totals.
      </p>
    </div>
  );
}
